import {
  rem,
  Button,
  Group,
  createStyles,
  Modal,
  Loader,
  Select,
  Menu,
  UnstyledButton,
  ScrollArea,
  Stack,
  Text,
  Image,
  Alert,
} from "@mantine/core";
import { IconBrain, IconChevronDown, IconChevronsDown, IconRocket } from "@tabler/icons-react";
import { DatePicker } from "@mantine/dates";

import React, { useEffect, useState } from "react";

import fetchApi from "../../Lib/api";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import useUserStore from "../../../stores/userStore";
import { useQuery } from "@tanstack/react-query";
import UserHeader from "../../../stories/UserHeader";
import SidebarAction from "../../../stories/SidebarAction";
import StartupCards from "../../../stories/StartupCards";
import SearchTags from "../../../stories/SearchTags";
import styled from "styled-components";
import StartupFooter from "../../../stories/StartupFooter";
import { useFilterStore } from "../../../stores/useFilterStore";
import moment from "moment";
import { IconFlame } from "@tabler/icons-react";
const HeadTitle = styled.h3`
  font-size: 20px;
  font-weight: bold;
  color: #333333;
  margin-bottom: 6px;
`;

const Subtitle = styled.p`
  font-size: 12px;
  color: #666666;
  margin-bottom: 16px;
  max-width: 80ch;
`;

const useStyles = createStyles((theme) => ({
  control: {
    minWidth: rem(150),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: [theme.spacing.xs, theme.spacing.md],
    borderRadius: theme.radius.md,
    border: `1px solid ${theme.colors.gray[4]}`,
    transition: "background-color 150ms ease",
    backgroundColor: "white",
    "&[data-expanded]": {
      backgroundColor: theme.colors.gray[2],
    },
    "&:hover": {
      backgroundColor: theme.colors.gray[2],
    },
  },
  label: {
    fontWeight: 600,
    fontSize: theme.fontSizes.sm,
    color: theme.colors.gray[6],
    marginRight: theme.spacing.md,
  },
  icon: {
    transition: "transform 150ms ease",
    transform: "rotate(0deg)",
    "[data-expanded] &": {
      transform: "rotate(180deg)",
    },
  },
}));

export function DropDownSelect2({
  selectType,
  selectData,
  setFilterParams,
  filterKey,
  date,
}) {
  const [opened, setOpened] = useState(false);
  const [selected, setSelected] = useState(selectType);
  const { classes, cx } = useStyles();

  const handleSelect = (item) => {
    if (item.label === "All") {
      setSelected(selectType);
      setFilterParams((prevParams) => {
        const { [filterKey]: _, ...updatedParams } = prevParams;

        return updatedParams;
      });
      return;
    }
    setSelected(item);
    setFilterParams((p) => ({ ...p, [filterKey]: item.value || item.label }));
    console.log({ [filterKey]: item.label });
  };

  const items = selectData.map((item, index) => (
    <Menu.Item onClick={() => handleSelect(item)} key={item.label + index}>
      {item.label}
    </Menu.Item>
  ));

  return (
    <Menu
      style={{ backgroundColor: "white", minWidth: "100%" }}
      onOpen={() => setOpened(true)}
      onClose={() => setOpened(false)}
      radius="md"
      width={date ? "unset" : "target"}
      withArrow
      withinPortal
    >
      <Menu.Target>
        <UnstyledButton
          className={classes.control}
          style={{ backgroundColor: "white" }}
        >
          <Group gap="xs">
            <span className={classes.label}>{selected.label}</span>
          </Group>
          <IconChevronDown size="1rem" className={classes.icon} stroke={1.5} />
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown w={500}>
        {!date ? (
          <>
            <Menu.Label>{selected.label}</Menu.Label>
            <ScrollArea h={200}> {items}</ScrollArea>
          </>
        ) : (
          <Stack className="text-center">
            <Button
              variant="light"
              compact
              onClick={(d) => handleSelect({ label: "All" })}
            >
              Reset
            </Button>
            <DatePicker
              onChange={(d) =>
                handleSelect({ label: moment(d).format("YYYY-MM-DD") })
              }
            />
          </Stack>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}

export function DropDownSelect({ selectType, selectData, filterKey, date }) {
  // Get the setter and the current filterParams from the global store
  const setFilterParam = useFilterStore((state) => state.setFilterParam);
  const filterParams = useFilterStore((state) => state.filterParams);
  const isMobile = useMediaQuery('(max-width: 834px)');

  // Use the current value from the global store, falling back to the default provided by selectType
  const currentValue = filterParams[filterKey] || selectType?.value || "";

  // Transform selectData to the shape expected by Mantine's Select:
  // an array of objects with { value, label }
  const data = selectData.map((item) => ({
    value: item.value || item.label,
    label: item.label,
  }));

  // Handle changes from the Select component
  const handleChange = (value) => {
    if (value === "All" || !value) {
      // If "All" or an empty value is selected, remove the filter
      setFilterParam(filterKey, undefined);
    } else {
      setFilterParam(filterKey, value);
    }
  };

  return (
    <Select
      data={data}
      searchable
      size={isMobile ? "sm" : "lg"}
      clearable
      value={currentValue}
      onChange={handleChange}
      placeholder={selectType?.label || "Select"}
      rightSection={<IconChevronsDown size="1rem" />}
      rightSectionWidth={30}
      styles={{
        input: {
          '::placeholder': {
            color: '#000000', // black
            opacity: 1,       // ensure it's not faded
          },
           borderRadius: '8px',
           backgroundColor: 'white'
        },
        rightSection: { pointerEvents: 'none' }
      }}
    />
  );
}

export function InputWithButton({ placeholder, setFilterParams, buttonText }) {
  const [value, setValue] = useState("");
  return (
    <div className="relative w-full lg:w-2/3 ">
      <div className="absolute inset-y-0 start-0 flex items-center ps-4 pointer-events-none">
        <svg
          className="w-8 h-8 text-gray-500"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 20 20"
        >
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
          />
        </svg>
      </div>
      <input
        type="search"
        id="default-search"
        className="block w-full p-4 ps-16 text-xl text-gray-900 border-none outline-none rounded-lg  focus:ring-red-500 focus:border-red-500"
        placeholder={placeholder}
        required
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.target.value.trim() === "") {
            setFilterParams((prevParams) => {
              const { search_term: _, ...updatedParams } = prevParams;

              return updatedParams;
            });
            return;
          }
          if (e.key === "Enter" && e.target.value.trim() !== "") {
            setFilterParams((p) => ({
              ...p,
              search_term: e.target.value.trim(),
            }));
          }
        }}
      />
      <button
        onClick={(e) => {
          if (value.trim() === "") {
            setFilterParams((prevParams) => {
              const { search_term: _, ...updatedParams } = prevParams;

              return updatedParams;
            });
            return;
          }
          setFilterParams((p) => ({
            ...p,
            search_term: value.trim(),
          }));
        }}
        className="text-white absolute end-2.5 inset-y-2.5 bg-blue-800 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-4 py-2 "
      >
        {buttonText}
      </button>
    </div>
  );
}

export function CreateAction({}) {
  const [opened, { close, open }] = useDisclosure(false);
  const { user, is_notion_connected } = useUserStore((state) => ({
    user: state.user.user,
    is_notion_connected: state.is_notion_connected,
  }));
  const { t } = useTranslation();
  const [notionModalOpen, setNotionModalOpen] = useState(false);
  const handleNotionConnect = () => {
    setNotionModalOpen(true);
  };
  const redirectToNotionAuth = () => {
    const currentUrl = window.location.href; // where the user is now
    window.location.href = `/auth/notion?return_url=${encodeURIComponent(currentUrl)}`;
  };
  return (
    <>
      {opened && (
        <Modal
          opened={opened}
          onClose={close}
          size="lg"
          withCloseButton={false}
        >
          <div class="p-4 overflow-y-auto">
            <p class="mt-1 text-gray-8000">
              {t("modal.profile_access_message")}
            </p>
          </div>
          <div class="flex justify-end items-center gap-x-2 py-3 px-4">
            <Button onClick={close} variant="default" className=" px-12">
              {t("modal.cancel_button")}
            </Button>
            <Button
              href="/onboarding"
              component="a"
              variant="light"
              color="indigo"
              className="text-blue-700 px-12"
            >
              {t("modal.complete_profile_button")}
            </Button>
          </div>
        </Modal>
      )}
      <Group className="flex-nowrap overflow-auto">
        {/* Notion Warning Modal */}
      <Modal
        opened={notionModalOpen}
        onClose={() => setNotionModalOpen(false)}
        centered
        withCloseButton
        size="lg"
        overlayProps={{ opacity: 0.55, blur: 3 }}
      >
        <Stack align="center" spacing="lg">
          <Text size="xl" weight={600} ta="center" className="text-gray-800">
            Important: Notion Projects Page Required
          </Text>

          <Text size="sm" ta="left" className="text-gray-600 max-w-md">
            Please make sure your Notion workspace contains the <strong>“Projects”</strong> template. This is required for integration to work properly. On the next screen, you should select and authorize NowCollab with this page. If the page is not there, please create it and try connecting again.
          </Text>

          <Image
            src="/notion_project_missing.png"
            alt="Notion Project Missing"
            w={350}
            h="auto"
            fit="contain"
            radius="md"
            className="shadow-md border"
          />

          <Group position="right" className="w-full mt-4">
            <Button  variant="light" color="red" onClick={() => setNotionModalOpen(false)} className="px-6">
              Cancel
            </Button>
            <Button onClick={redirectToNotionAuth} className="px-6">
              Proceed
            </Button>
          </Group>
        </Stack>
       </Modal>

        <Button
          component={
            user.profile_complete || !user.validated_once ? "a" : "button"
          }
          href={
            user.profile_complete || !user.validated_once
              ? "/ai_validation_tool"
              : undefined
          }
          onClick={
            !user.profile_complete && user.validated_once ? open : undefined
          }
          variant="outline"
          fullWidth
          className="w-full font-light rounded-lg border border-transparent bg-blue-800 text-white hover:bg-blue-600 disabled:opacity-50 disabled:pointer-events-none create-startup"
          leftIcon={<IconBrain size="1.5rem" />}
          size="md"
        >
          {t("startups.validate_and_create")}
        </Button>
        <Button
          variant="default"
          className="w-full lg:w-1/2 flex-grow-0 font-light border border-transparent bg-blue-600 text-white hover:bg-blue-600 disabled:opacity-50 disabled:pointer-events-none"
          leftIcon={<IconRocket size="1.5rem" />}
          size="md"
          component={user.profile_complete ? "a" : "button"}
          href={user.profile_complete ? "/project_entities/new" : undefined}
          onClick={!user.profile_complete ? open : undefined}
        >
          {t("startups.create_project")}
        </Button>
        {!is_notion_connected ? (
          <Button className="w-full lg:w-1/2 flex-grow-0 font-light border border-transparent bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 disabled:pointer-events-none"
           size="md"
           onClick={handleNotionConnect}>
            Add Notion Projects
          </Button>
        ) : (
          <Button 
          size="md"
          disabled>
            Notion Connected
          </Button>
        )}
      </Group>
    </>
  );
}

const useFilteredStartups = (filterParams) => {
  // Convert filterParams to query string
  const params = new URLSearchParams();
  for (const key in filterParams) {
    params.append(`q[${key}]`, filterParams[key]);
  }
  const queryString = params.toString();

  return useQuery({
    queryKey: ["filtered-startups", queryString], // Query key includes filter params
    queryFn: async () => {
      const response = await fetchApi(
        `/startup_entities?${queryString}`,
        "GET"
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch startups");
      }

      return await response.json();
    },
    retry: 1, // Retry once on failure
    staleTime: 5 * 60 * 1000, // Data is fresh for 5 minutes
    cacheTime: 30 * 60 * 1000, // Cache inactive data for 30 minutes
  });
};

export default function Startups() {
  // Get global filter parameters including the search term
  const filterParams = useFilterStore((state) => state.filterParams);
  const searchTerm = filterParams.search_term;

  const [startups, setStartups] = useState([]);
  const [searchStartups, setSearchStartups] = useState([]);
  const [similarStartups, setSimilarStartups] = useState([]);
  const { t } = useTranslation();

  const { data, isLoading, isError, error } = useFilteredStartups(filterParams);

  useEffect(() => {
    if (data) {
      // Default categorized startups (for non-search view)
      setStartups(data);
      // For search, backend returns "Exact Matches" and "Similar Startups"
      setSearchStartups(data["Exact Matches"] || []);
      setSimilarStartups(data["Similar Startups"] || []);
    }
  }, [data]);

  if (isLoading) {
    return (
      <Loader
        className="mx-auto mt-10"
        color="indigo"
        size="lg"
        variant="dots"
      />
    );
  }

  if (isError) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <main className="flex flex-col gap-10 justify-center px-4">
      <UserHeader />
      {searchTerm ? (
        // When a search term exists, render both Exact Matches and Similar Startups
        <div>
          <HeadTitle>Exact Matches</HeadTitle>
          <Subtitle>Projects that exactly match your search query.</Subtitle>
          {searchStartups.length > 0 ? (
            <StartupCards allCards={searchStartups} />
          ) : (
            <p>No exact matches found.</p>
          )}

          <HeadTitle>Similar Startups</HeadTitle>
          <Subtitle>Projects similar to your search query.</Subtitle>
          {similarStartups.length > 0 ? (
            <StartupCards allCards={similarStartups} />
          ) : (
            <p>No similar startups found.</p>
          )}
        </div>
      ) : (
        // Default view when no search term is provided
        <>
          <SidebarAction new_user={true} />
          <div>
            <HeadTitle>
              <Group gap="xs" align="center">
                <IconFlame size={20} color="orange" />
               {t("startups.featured_projects")}
              </Group>
            </HeadTitle>
            <Subtitle>
            {t("startups.featured_projects_subheading")}
            </Subtitle>
            <StartupCards
              allCards={startups["Featured Projects"] || []}
            />
          </div>

          <SearchTags />

          <div>
            <HeadTitle>Projects open for applications</HeadTitle>
            <Subtitle>Apply to join these projects.</Subtitle>
            <StartupCards
              allCards={startups["Open for Applications"] || []}
            />
          </div>

          <div>
            <HeadTitle>Explore Projects Tailored for You</HeadTitle>
            <Subtitle>
              Discover more projects based on your profile and activity.
            </Subtitle>
            <StartupCards
              allCards={startups["Tailored For You"] || []}
            />
          </div>
        </>
      )}
      <StartupFooter />
    </main>
  );
}
